import React from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiCalendar, FiBook } from 'react-icons/fi';
import { Education as EducationType } from '../types/resume.types';

interface EducationProps {
  education: EducationType[];
}

const EducationCard: React.FC<{ education: EducationType; index: number }> = ({ education, index }) => {
  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: index * 0.2 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="bg-white dark:bg-gray-800 p-8 rounded-2xl card-shadow hover:shadow-xl transition-all duration-300"
    >
      <div className="flex items-start space-x-4">
        <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <FiBook className="text-white" size={28} />
        </div>
        
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {education.degree}
          </h3>
          {education.field && (
            <p className="text-primary-600 dark:text-primary-400 font-semibold mb-2">
              {education.field}
            </p>
          )}
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-3">
            {education.institution}
          </p>
          <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
            <FiCalendar size={16} />
            <span className="text-sm">{education.duration}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Education: React.FC<EducationProps> = ({ education }) => {
  return (
    <section id="education" className="py-16 section-padding bg-gray-50 dark:bg-gray-800/50">
      <div className="container-width">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Education
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            Academic foundation spanning Information Technology and Finance
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mt-4"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {education.map((edu, index) => (
            <EducationCard key={index} education={edu} index={index} />
          ))}
        </div>

        {/* Education journey visualization */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl card-shadow max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <FiBookOpen className="text-primary-500" size={24} />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Educational Journey
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              My educational background combines <span className="font-semibold text-primary-600 dark:text-primary-400">technical expertise in Information Technology</span> with 
              <span className="font-semibold text-secondary-600 dark:text-secondary-400"> business acumen from Finance and Banking</span>. 
              This unique combination provides me with both the technical skills needed for software testing and the analytical mindset essential for understanding business requirements and user needs.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Education;